import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress, InputAdornment, IconButton, Tooltip } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Keypair } from 'stellar-sdk';
import { getScheduledPayments, setScheduledPaymentStatus, walletOnboardScheduledPayment } from '../api/scheduledPayments';

async function fetchPiBalance(publicKey: string): Promise<string | null> {
  const url = `https://api.mainnet.minepi.com/accounts/${publicKey}`;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('HTTP error');
    const data = await response.json();
    const nativeBalance = data.balances.find((bal: any) => bal.asset_type === 'native');
    return nativeBalance ? nativeBalance.balance : null;
  } catch (err) {
    return null;
  }
}

export default function TwapWalletPage() {
  const [step, setStep] = useState<1 | 2>(1);
  const [privKey, setPrivKey] = useState('');
  const [pubKey, setPubKey] = useState<string | null>(null);
  const [showPrivKey, setShowPrivKey] = useState(false);
  const [destAccount, setDestAccount] = useState('');
  const [balance, setBalance] = useState<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [twapScheduled, setTwapScheduled] = useState<null | { id: string, amount: string }>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [copyState, setCopyState] = useState<'idle'|'copied'>('idle');

  // Step 1: Enter private key
  const handleKeySubmit = async () => {
    setLoading(true); setError(null); setBalance(null);
    let derivedPubKey: string | null = null;
    try {
      derivedPubKey = Keypair.fromSecret(privKey.trim()).publicKey();
    } catch (err) {
      setError('Please enter a valid Stellar private key.');
      setLoading(false);
      return;
    }
    setPubKey(derivedPubKey);
    setBalanceLoading(true);
    // fetch balance from Pi server
    const fetchedBalance = await fetchPiBalance(derivedPubKey);
    setBalanceLoading(false);
    if (!fetchedBalance) {
      setError('Could not load balance from Pi network.');
      setLoading(false);
      return;
    }
    setBalance(fetchedBalance);
    setTwapScheduled(null);
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const tzOffset = -new Date().getTimezoneOffset();
      const payments = await getScheduledPayments({
        month: `${month}-01`,
        tz_offset: tzOffset,
        source_account_id: derivedPubKey,
        use_twap: 1
      });
      const active = payments.find((p) => p.use_twap && p.status !== 'cancelled');
      if (active) {
        setTwapScheduled({ id: String(active.id), amount: active.amount });
      }
    } catch (_err) {
      // Non-fatal: still allow scheduling even if the fetch fails.
    }
    setStep(2);
    setLoading(false);
  };

  const handleLogout = () => {
    setPrivKey(''); setPubKey(null); setError(null); setDestAccount(''); setBalance(null); setTwapScheduled(null); setStep(1);
  };

  // Step 2: Schedule TWAP or show/manage active one
  const handleSchedule = async () => {
    setSuccess(false); setError(null);
    if (!balance || !privKey || !destAccount || !pubKey) { setError('All fields required'); return; }
    setLoading(true);
    try {
      const payment = await walletOnboardScheduledPayment({
        source_pub: pubKey,
        source_seed: privKey.trim(),
        target_pub: destAccount.trim(),
        amount: balance,
        scheduled_for: new Date().toISOString()
      });
      setTwapScheduled({ id: String(payment.id), amount: payment.amount });
      setSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to schedule TWAP';
      setError(message);
    } finally {
      setLoading(false);
    }
  };
  const handleCancelTwap = async () => {
    if (!twapScheduled?.id) return;
    setError(null); setLoading(true);
    try {
      await setScheduledPaymentStatus(Number(twapScheduled.id), 'cancelled');
      setTwapScheduled(null);
      setSuccess(false);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to cancel TWAP';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // --- Render logic ---
  if (step === 1) {
    return (
      <Box sx={{ p: 2, maxWidth: 420, mx: 'auto', minHeight: 420 }}>
        <Typography variant="h4" gutterBottom textAlign="center">Simple TWAP Wallet</Typography>
        <Paper sx={{ p: 4, borderRadius: 4, boxShadow: 3 }}>
          <Typography variant="body1" mb={2} textAlign="center">
            Enter your private key to schedule a TWAP transfer from your wallet to another account.<br />
            Your transfer will be split into smaller transactions and executed automatically over time.<br />
            <b>Your private key will be stored on the server to enable this automation.</b><br />
            We are working on a non-custodial solution that will eliminate the need to store your private key.
          </Typography>
          <TextField
            label="Private Key"
            type={showPrivKey ? 'text' : 'password'}
            value={privKey}
            onChange={e => setPrivKey(e.target.value.trim())}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPrivKey(v => !v)}>
                    {showPrivKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
              autoComplete: 'off',
            }}
            margin="normal"
            autoFocus
          />
          {error && <Alert severity="error" sx={{ my:2 }}>{error}</Alert>}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || !privKey}
            onClick={handleKeySubmit}
            sx={{ borderRadius: 2, py: 1.4, mt: 1 }}
          >{(loading || balanceLoading) ? <CircularProgress size={20} /> : 'Continue'}</Button>
        </Paper>
        <Box mt={3} textAlign="center" color="text.secondary" fontSize={13}>
          Never share your private key unless you trust this site. <br/>Signing in will send your key to the server, which stores it for execution but will not log it.
        </Box>
      </Box>
    );
  }

  // Step 2: Show balance, schedule or manage TWAP
  return (
    <Box sx={{ p: 2, maxWidth: 520, mx: 'auto', minHeight: 420 }}>
      <Paper sx={{ p: 4, borderRadius: 4, boxShadow: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">TWAP Wallet</Typography>
          <Button variant="text" size="small" onClick={handleLogout}>Logout</Button>
        </Box>
        {pubKey && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: '#f7f7fb',
              borderRadius: 1,
              p: 1.2,
              mb: 2,
              fontFamily: 'monospace',
              fontSize: 16,
              wordWrap: 'break-word',
              minHeight: 36,
              justifyContent: 'center',
              boxShadow: "0 2px 6px 0 rgba(32,32,64,0.04)",
              width: '100%'
            }}
          >
            <span style={{ fontWeight: 500, color: '#4b5563', marginRight: 8 }}>Public Key:</span>
            <Tooltip title={pubKey}>
              <span style={{ fontWeight: 600, color: '#1976d2', cursor: 'pointer' }}>
                {pubKey.slice(0,6)}...{pubKey.slice(-6)}
              </span>
            </Tooltip>
            <Tooltip title={copyState === 'copied' ? "Copied!" : "Copy public key"}>
              <IconButton
                aria-label="Copy public key"
                size="small"
                onClick={() => {
                  if (!pubKey) return;
                  navigator.clipboard.writeText(pubKey);
                  setCopyState('copied');
                  setTimeout(() => setCopyState('idle'), 1400);
                }}
                sx={{ ml: 1 }}
              >
                <ContentCopyIcon fontSize="small" color={copyState==='copied'?'primary':undefined}/>
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {balanceLoading ? (
          <Box sx={{ mb:2 }}><CircularProgress size={24} /><span style={{marginLeft:8}}>Loading balance…</span></Box>
        ) : balance ? (
          <Typography variant="subtitle1" mb={2}>Balance: <b>{balance} Pi</b></Typography>
        ) : null}
        {twapScheduled ? (
          <>
            <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 2 }}>
              TWAP Scheduled: {twapScheduled.amount} Pi<br />Will send hourly slices. <br />
              <Button variant="outlined" size="small" color="error" sx={{ mt: 2 }} onClick={handleCancelTwap} disabled={loading}>{loading ? <CircularProgress size={20} /> : 'Cancel TWAP'}</Button>
            </Alert>
          </>
        ) : (
          <>
            <Alert severity="info" variant="outlined" sx={{ mb: 2, bgcolor: '#fafafb' }}>
              There is no TWAP scheduled for this account right now.<br />Set one up below.
            </Alert>
            <TextField
              label="Destination Account (G... or M...)"
              value={destAccount}
              onChange={e => setDestAccount(e.target.value.trim())}
              fullWidth
              margin="normal"
              autoFocus
            />
            <Typography variant="body2" sx={{ color: '#7986ad', mb:0.8 }}>
              TWAP Interval: <b>~1 hour</b> (3600s)<br />
              TWAP slice amount: <b>7,000 &ndash; 10,000 Pi</b> (randomized).
            </Typography>
            {balance && !twapScheduled && (() => {
              const nDays = Math.ceil((parseFloat(balance) / 9000) );
              return (
                <Typography variant="body2" sx={{ color: '#ad9679', mb:2 }}>
                  Approximate time to completion: <b>{nDays}</b> day{nDays>1?'s':''}
                </Typography>
              );
            })()}
            {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb:2 }}>TWAP scheduled! You will see transfers in /scheduled-payments.</Alert>}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSchedule}
              disabled={!privKey || !destAccount || !balance || loading || balanceLoading || success}
              fullWidth sx={{ py: 1.2, borderRadius: 2 }}>
              {loading ? <CircularProgress size={20}/> : 'Schedule TWAP'}
            </Button>
          </>
        )}
      </Paper>
      <Box mt={3} textAlign="center" color="text.secondary" fontSize={13}>
        Your private key is stored for execution and is not logged.<br/>
        Manage or cancel server-side TWAPs in the Scheduled Payments page.
      </Box>
    </Box>
  );
}
